<template>
    <div class="drawer-container">
        <transition name="backdrop">
            <div v-if="isOpen" class="drawer-backdrop" :style="backdropStyle" @click.prevent="close()">
                <slot name="aside" />
            </div>
        </transition>
        <transition name="drawer">
            <div v-if="isOpen" class="drawer" :class="size" :style="drawerStyle">
                <div class="drawer-title tw-px-6">
                    <slot v-if="$slots['title']" name="title" />
                    <span>{{ title }}</span>

                    <button class="tw-ml-auto tw-size-10" @click="close()">
                        <i class="mdi mdi-close tw-text-2xl"></i>
                    </button>
                </div>
                <div v-if="$slots['header']" class="drawer-header tw-px-6 tw-py-3"><slot name="header"></slot></div>
                <div
                    class="drawer-content"
                    :class="{
                        'tw-px-6 tw-mt-5': !paddingless,
                    }"
                >
                    <slot></slot>
                </div>
                <div v-if="$slots['footer']" class="drawer-footer tw-px-3 tw-py-3"><slot name="footer"></slot></div>
                <Loader v-if="loading" fill />
            </div>
        </transition>
    </div>
</template>
<script>
import Loader from '@/js/components/controls/Loader.vue';
import { useOverlayZIndex } from '@/js/composables/useOverlayZIndex.ts';

export default {
    components: {
        Loader,
    },
    props: {
        title: {
            type: String,
            default: '',
        },
        size: {
            type: String,
            default: '',
        },
        loading: {
            type: Boolean,
            default: false,
        },
        paddingless: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['opened', 'closed', 'opening', 'closing'],
    data() {
        return {
            isOpen: false,
            activeZIndex: null,
        };
    },
    computed: {
        drawerStyle() {
            return this.activeZIndex ? { zIndex: this.activeZIndex } : null;
        },
        backdropStyle() {
            return this.activeZIndex ? { zIndex: this.activeZIndex - 1 } : null;
        },
    },
    watch: {
        isOpen(newVal) {
            if (newVal) {
                this.allocateZIndex();
                document.body.style.overflow = 'hidden';
                document.body.style.paddingRight = '15px';
                this.$emit('opened');
            } else {
                document.body.style.overflow = null;
                document.body.style.paddingRight = null;
                this.$emit('closed');
            }
        },
    },
    methods: {
        allocateZIndex() {
            const { activeZIndex, allocateZIndex } = useOverlayZIndex();

            allocateZIndex();
            this.activeZIndex = activeZIndex.value;
        },
        open() {
            this.$emit('opening');
            this.isOpen = true;
        },
        close() {
            this.$emit('closing');
            this.isOpen = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.drawer-container {
    .drawer {
        position: fixed;
        top: 0;
        right: 0;
        width: min(500px, 100vw);
        height: 100vh;
        background: white;
        display: flex;
        flex-direction: column;
        box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.1);
        z-index: 5000;

        &.is-small {
            width: min(350px, 100vw);
        }
        &.is-large {
            width: min(750px, 100vw);
        }
        &.is-xlarge {
            width: min(900px, 100vw);
        }

        & > .drawer-title {
            display: flex;
            align-items: center;
            @apply tw-bg-eventwise-dark tw-text-lg;
            border-bottom: 1px solid rgba(0, 0, 0, 0.05);
            min-height: 60px;
            color: white;
        }

        & > .drawer-header:not(:empty) {
            border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        }

        & > .drawer-content {
            overflow-x: hidden;
            flex: 1;
        }

        & > .drawer-footer:not(:empty) {
            border-top: 1px solid rgba(0, 0, 0, 0.05);
        }
    }

    .drawer-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 4999;
        background: rgba(black, 0.33);
    }
}

.drawer-enter-active,
.drawer-leave-active {
    transition: all 0.25s ease;
    transform: scaleX(1) scaleY(1) translateX(0);
    opacity: 1;
}
.drawer-enter,
.drawer-leave-to {
    transform: scaleX(0.5) scaleY(0.8) translateX(100%);
    opacity: 0;
}

.backdrop-enter-active,
.backdrop-leave-active {
    transition: all 0.33s ease;
    opacity: 1;
}
.backdrop-enter,
.backdrop-leave-to {
    opacity: 0;
}
</style>
